import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import "@/App.css";

// Import components
import { Button } from "./components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Textarea } from "./components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Badge } from "./components/ui/badge";
import { Separator } from "./components/ui/separator";
import { Progress } from "./components/ui/progress";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "./components/ui/alert-dialog";
import { toast, Toaster } from "sonner";
import { PlusCircle, FileText, Download, Users, Building2, CheckCircle2, Edit3, Trash2, ArrowRight, ArrowLeft, ExternalLink, Clock, FileCheck, UserCheck, Copy, Eye, AlertCircle } from "lucide-react";

const FUNCTION_BASE_URL =
  process.env.REACT_APP_FUNCTION_URL ||
  "https://us-central1-creazioneimpresa.cloudfunctions.net/apiWorkForEquity";
const API = `${FUNCTION_BASE_URL}/api`;
const CLIENT_BASE_URL = window.location.origin;

const toDateFromTimestamp = (value) => {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === "string") {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  if (typeof value === "object") {
    if (typeof value.toDate === "function") {
      const converted = value.toDate();
      return Number.isNaN(converted.getTime()) ? null : converted;
    }
    const seconds = value.seconds ?? value._seconds;
    const nanoseconds = value.nanoseconds ?? value._nanoseconds ?? 0;
    if (typeof seconds === "number") {
      return new Date(seconds * 1000 + Math.floor(nanoseconds / 1e6));
    }
  }
  return null;
};

const formatDateDisplay = (value) => {
  const date = toDateFromTimestamp(value);
  return date ? date.toLocaleDateString("it-IT") : "";
};

// Utility function for Italian Fiscal Code validation
const validateCodiceFiscale = (cf) => {
  if (!cf) return false;
  
  // Remove spaces and convert to uppercase
  cf = cf.replace(/\s/g, '').toUpperCase();
  
  // Check length
  if (cf.length !== 16) return false;
  
  // Check pattern (15 alphanumeric chars + 1 letter)
  const pattern = /^[A-Z]{6}[0-9]{2}[A-Z][0-9]{2}[A-Z][0-9]{3}[A-Z]$/;
  if (!pattern.test(cf)) return false;
  
  // Check control character (last letter)
  const evenMap = { '0': 0, '1': 1, '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8, '9': 9, 'A': 0, 'B': 1, 'C': 2, 'D': 3, 'E': 4, 'F': 5, 'G': 6, 'H': 7, 'I': 8, 'J': 9, 'K': 10, 'L': 11, 'M': 12, 'N': 13, 'O': 14, 'P': 15, 'Q': 16, 'R': 17, 'S': 18, 'T': 19, 'U': 20, 'V': 21, 'W': 22, 'X': 23, 'Y': 24, 'Z': 25 };
  const oddMap = { '0': 1, '1': 0, '2': 5, '3': 7, '4': 9, '5': 13, '6': 15, '7': 17, '8': 19, '9': 21, 'A': 1, 'B': 0, 'C': 5, 'D': 7, 'E': 9, 'F': 13, 'G': 15, 'H': 17, 'I': 19, 'J': 21, 'K': 2, 'L': 4, 'M': 18, 'N': 20, 'O': 11, 'P': 3, 'Q': 6, 'R': 8, 'S': 12, 'T': 14, 'U': 16, 'V': 10, 'W': 22, 'X': 25, 'Y': 24, 'Z': 23 };
  
  let sum = 0;
  for (let i = 0; i < 15; i++) {
    const char = cf[i];
    if (i % 2 === 0) {
      sum += oddMap[char];
    } else {
      sum += evenMap[char];
    }
  }
  
  const remainder = sum % 26;
  const expectedControl = String.fromCharCode(65 + remainder);
  
  return cf[15] === expectedControl;
};

// Utility function for Italian VAT number validation
const validatePartitaIVA = (piva) => {
  if (!piva) return false;
  
  // Remove spaces and keep only digits
  piva = piva.replace(/\s/g, '');
  
  // Check if it's exactly 11 digits
  if (!/^\d{11}$/.test(piva)) return false;
  
  // Validate check digit
  let sum = 0;
  for (let i = 0; i < 10; i++) {
    let digit = parseInt(piva[i]);
    if (i % 2 === 1) {
      digit *= 2;
      if (digit > 9) digit = digit - 9;
    }
    sum += digit;
  }
  
  const checkDigit = (10 - (sum % 10)) % 10;
  return checkDigit === parseInt(piva[10]);
};

// Client Assessment Form Component
const ClientAssessmentForm = () => {
  const { slug } = useParams();
  const [assessment, setAssessment] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    societa_destinataria: {
      denominazione: "",
      partita_iva: "",
      indirizzo_sede: "",
      cap: "",
      citta_provincia: "",
      legale_rappresentante_nome: "",
      legale_rappresentante_cf: "",
      legale_rappresentante_luogo_nascita: "",
      legale_rappresentante_data_nascita: "",
      mail_pec: "",
      codice_sdi: ""
    },
    beneficiari: [],
    attivita_contratti: [],
    valutazione_complessiva: {
      valore_premoney: 0,
      aucap_deliberato: false,
      aucap_include_wfe: null,
      data_atto_aucap: "",
      data_limite_aucap: ""
    }
  });

  const totalSteps = 4;
  const progress = (currentStep / totalSteps) * 100;

  useEffect(() => {
    loadAssessment();
  }, [slug]);

  const loadAssessment = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/client/${slug}`);
      const assessmentData = response.data;
      
      setAssessment(assessmentData);
      
      // If already completed, show submitted state
      if (assessmentData.compilato_da_cliente) {
        setSubmitted(true);
        setFormData({
          societa_destinataria: assessmentData.societa_destinataria,
          beneficiari: assessmentData.beneficiari,
          attivita_contratti: assessmentData.attivita_contratti,
          valutazione_complessiva: assessmentData.valutazione_complessiva
        });
      }
    } catch (error) {
      console.error("Error loading assessment:", error);
      toast.error("Assessment non trovato o non valido");
    } finally {
      setLoading(false);
    }
  };

  const updateFormData = (section, data) => {
    setFormData(prev => ({
      ...prev,
      [section]: data
    }));
  };

  const addBeneficiario = () => {
    const newBeneficiario = {
      id: Date.now().toString(),
      tipo: "persona_fisica",
      persona_fisica: {
        nome: "",
        cognome: "",
        codice_fiscale: "",
        luogo_nascita: "",
        data_nascita: "",
        indirizzo_domicilio: "",
        mail_pec: "",
        mail_ordinaria: ""
      },
      persona_giuridica: null
    };
    
    updateFormData("beneficiari", [...formData.beneficiari, newBeneficiario]);
  };

  const removeBeneficiario = (id) => {
    updateFormData("beneficiari", formData.beneficiari.filter(b => b.id !== id));
    updateFormData("attivita_contratti", formData.attivita_contratti.filter(a => a.beneficiario_id !== id));
  };

  const addAttivitaContratto = (beneficiarioId) => {
    const newAttivita = {
      id: Date.now().toString(),
      beneficiario_id: beneficiarioId,
      descrizione_attivita_1: "",
      descrizione_attivita_2: "",
      durata_mesi: 0,
      importo_obiettivo: 0,
      numero_ore_previste: 0,
      costo_orario: 0,
      equity_percentuale: 0,
      cliff_descrizione: "",
      performance_descrizione: ""
    };
    
    updateFormData("attivita_contratti", [...formData.attivita_contratti, newAttivita]);
  };

  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      await axios.put(`${API}/client/${slug}/submit`, formData);
      
      toast.success("Assessment inviato con successo! Il contratto sarà generato automaticamente.");
      setSubmitted(true);
    } catch (error) {
      console.error("Error submitting assessment:", error);
      toast.error("Errore nell'invio dell'assessment");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Caricamento assessment...</p>
        </div>
      </div>
    );
  }

  if (!assessment) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <h1 className="text-xl font-semibold text-red-600 mb-4">Assessment Non Trovato</h1>
            <p className="text-slate-600">Il link fornito non è valido o l'assessment è scaduto.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <CheckCircle2 className="w-16 h-16 text-green-600 mx-auto mb-4" />
            <h1 className="text-2xl font-semibold text-slate-800 mb-4">Assessment Completato!</h1>
            <p className="text-slate-600 mb-6">
              Grazie per aver compilato l'assessment. I dati sono stati inviati e il contratto 
              sarà preparato dal nostro team.
            </p>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-green-800 text-sm">
                <strong>Prossimi passi:</strong><br />
                Il nostro team revisionerà i dati forniti e preparerà il contratto finale.
                Sarete contattati per eventuali chiarimenti o per la finalizzazione.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-6">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-800 mb-2">
            {assessment.titolo}
          </h1>
          <p className="text-slate-600">
            Compila tutti i campi richiesti per il tuo assessment Work for Equity
          </p>
          
          <div className="mt-6">
            <div className="flex items-center justify-between text-sm text-slate-600 mb-2">
              <span>Passaggio {currentStep} di {totalSteps}</span>
              <span>{Math.round(progress)}% completato</span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>
        </div>

        <Card className="shadow-lg border-0">
          <CardContent className="p-8">
            {currentStep === 1 && (
              <StepSocieta 
                data={formData.societa_destinataria} 
                onChange={(data) => updateFormData("societa_destinataria", data)} 
              />
            )}
            
            {currentStep === 2 && (
              <StepBeneficiari 
                data={formData.beneficiari}
                onChange={(data) => updateFormData("beneficiari", data)}
                onAdd={addBeneficiario}
                onRemove={removeBeneficiario}
              />
            )}
            
            {currentStep === 3 && (
              <StepAttivita 
                beneficiari={formData.beneficiari}
                attivita={formData.attivita_contratti}
                onChange={(data) => updateFormData("attivita_contratti", data)}
                onAdd={addAttivitaContratto}
              />
            )}
            
            {currentStep === 4 && (
              <StepValutazione 
                data={formData.valutazione_complessiva}
                onChange={(data) => updateFormData("valutazione_complessiva", data)}
              />
            )}

            <div className="flex justify-between mt-8 pt-6 border-t border-slate-200">
              <Button
                variant="outline"
                onClick={prevStep}
                disabled={currentStep === 1}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Indietro
              </Button>
              
              {currentStep < totalSteps ? (
                <Button
                  onClick={nextStep}
                  className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
                >
                  Avanti
                  <ArrowRight className="w-4 h-4" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Invio in corso...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4" />
                      Invia Assessment
                    </>
                  )}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Admin Dashboard Component
const AdminDashboard = () => {
  const [assessments, setAssessments] = useState([]);
  const [currentView, setCurrentView] = useState('list');
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newAssessmentTitle, setNewAssessmentTitle] = useState("");
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  useEffect(() => {
    loadAssessments();
  }, []);

  const loadAssessments = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/assessments`);
      setAssessments(response.data);
    } catch (error) {
      console.error("Error loading assessments:", error);
      toast.error("Errore nel caricamento degli assessments");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newAssessmentTitle.trim()) {
      toast.error("Inserisci un titolo per l'assessment");
      return;
    }

    try {
      const response = await axios.post(`${API}/assessments`, {
        titolo: newAssessmentTitle
      });
      
      await loadAssessments();
      setNewAssessmentTitle("");
      setShowCreateDialog(false);
      toast.success("Assessment creato! Condividi il link con il cliente.");
    } catch (error) {
      console.error("Error creating assessment:", error);
      toast.error("Errore nella creazione dell'assessment");
    }
  };

  const handleView = (assessment) => {
    setSelectedAssessment(assessment);
    setCurrentView('detail');
  };

  const handleDelete = async (assessmentId) => {
    try {
      await axios.delete(`${API}/assessments/${assessmentId}`);
      await loadAssessments();
      toast.success("Assessment eliminato con successo!");
    } catch (error) {
      console.error("Error deleting assessment:", error);
      toast.error("Errore nell'eliminazione dell'assessment");
    }
  };

  const handleApproval = async (assessmentId, stato, note = "") => {
    try {
      await axios.put(`${API}/assessments/${assessmentId}/approve`, {
        stato: stato,
        note_revisione: note
      });
      const updatedResponse = await axios.get(`${API}/assessments/${assessmentId}`);
      setSelectedAssessment(updatedResponse.data);
      await loadAssessments();
      toast.success(`Assessment ${stato === 'approvato' ? 'approvato' : 'messo in revisione'} con successo!`);
      return updatedResponse.data;
    } catch (error) {
      console.error("Error updating assessment:", error);
      toast.error("Errore nell'aggiornamento dell'assessment");
      return undefined;
    }
  };

  const getClientLink = (slug) => {
    return `${CLIENT_BASE_URL}/client/${slug}`;
  };

  const copyClientLink = (slug) => {
    const link = getClientLink(slug);
    navigator.clipboard.writeText(link);
    toast.success("Link copiato negli appunti!");
  };

  if (currentView === 'detail' && selectedAssessment) {
    return (
      <AssessmentDetail
        assessment={selectedAssessment}
        onBack={() => setCurrentView('list')}
        onApproval={handleApproval}
        onReload={loadAssessments}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Hero Section */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
                Gestione <span className="text-blue-600">Work for Equity</span>
              </h1>
              <p className="text-xl text-slate-600 mb-8 leading-relaxed">
                Crea assessment per i tuoi clienti e gestisci i contratti work for equity 
                in modo professionale e conforme alla normativa italiana.
              </p>
              <Button
                onClick={() => setShowCreateDialog(true)}
                size="lg"
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 px-8 py-3 text-lg"
                data-testid="create-assessment-btn"
              >
                <PlusCircle className="w-5 h-5" />
                Crea Nuovo Assessment
              </Button>
            </div>
            <div className="hidden lg:block">
              <img
                src="https://images.unsplash.com/photo-1521791055366-0d553872125f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMGNvbnRyYWN0fGVufDB8fHx8MTc1OTc2MjkxM3ww&ixlib=rb-4.1.0&q=85"
                alt="Business Contract"
                className="rounded-2xl shadow-2xl w-full h-96 object-cover"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">
              Assessment Creati
            </h2>
            <p className="text-slate-600">
              Gestisci tutti i tuoi assessment work for equity
            </p>
          </div>
          
          <Button
            onClick={() => setShowCreateDialog(true)}
            className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
            data-testid="create-new-assessment-btn"
          >
            <PlusCircle className="w-4 h-4" />
            Nuovo Assessment
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <AssessmentList
            assessments={assessments}
            onView={handleView}
            onDelete={handleDelete}
            onCopyLink={copyClientLink}
          />
        )}
      </div>

      {/* Create Assessment Dialog */}
      <AlertDialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Crea Nuovo Assessment</AlertDialogTitle>
            <AlertDialogDescription>
              Inserisci un titolo per il nuovo assessment. Verrà generato un link univoco da condividere con il cliente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Label htmlFor="assessment-title">Titolo Assessment</Label>
            <Input
              id="assessment-title"
              value={newAssessmentTitle}
              onChange={(e) => setNewAssessmentTitle(e.target.value)}
              placeholder="Es: Assessment WFE - Cliente ABC"
              className="mt-1"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={handleCreate}>
              Crea Assessment
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Toaster richColors position="top-right" />
    </div>
  );
};

// Assessment List Component for Admin
const AssessmentList = ({ assessments, onView, onDelete, onCopyLink }) => {
  const getStatusColor = (assessment) => {
    switch (assessment.stato) {
      case 'creato': return 'bg-gray-100 text-gray-800';
      case 'compilato_cliente': return 'bg-yellow-100 text-yellow-800';
      case 'in_revisione': return 'bg-blue-100 text-blue-800';
      case 'approvato': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (assessment) => {
    switch (assessment.stato) {
      case 'creato': return 'Da compilare';
      case 'compilato_cliente': return 'Compilato dal cliente';
      case 'in_revisione': return 'In revisione';
      case 'approvato': return 'Approvato';
      default: return assessment.stato;
    }
  };

  return (
    <div className="space-y-4">
      {assessments.length === 0 ? (
        <div className="text-center py-12 text-slate-500">
          <FileText className="w-16 h-16 mx-auto mb-4 text-slate-300" />
          <h3 className="text-lg font-medium mb-2">Nessun Assessment</h3>
          <p>Crea il tuo primo assessment per iniziare.</p>
        </div>
      ) : (
        assessments.map((assessment) => (
          <Card key={assessment.id} className="hover:shadow-md transition-shadow border-slate-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-slate-800">
                      {assessment.titolo}
                    </h3>
                    <Badge className={getStatusColor(assessment)}>
                      {getStatusText(assessment)}
                    </Badge>
                    {assessment.word_generato && (
                      <Badge variant="outline" className="text-green-600 border-green-600">
                        <FileCheck className="w-3 h-3 mr-1" />
                        Word
                      </Badge>
                    )}
                    {assessment.pdf_generato && (
                      <Badge variant="outline" className="text-red-600 border-red-600">
                        <FileText className="w-3 h-3 mr-1" />
                        PDF
                      </Badge>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-4 text-sm text-slate-500 mb-3">
                    <span>
                      Creato: {formatDateDisplay(assessment.data_creazione)}
                    </span>
                    {assessment.data_compilazione_cliente && (
                      <span>
                        <UserCheck className="w-4 h-4 inline mr-1" />
                        Compilato: {formatDateDisplay(assessment.data_compilazione_cliente)}
                      </span>
                    )}
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <code className="bg-slate-100 text-slate-800 px-2 py-1 rounded text-xs">
                        /client/{assessment.slug}
                      </code>
                    </div>
                    
                    {!assessment.compilato_da_cliente && (
                      <div className="flex items-center gap-2 text-sm">
                        <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
                        <span className="text-yellow-700 font-medium">
                          In attesa di compilazione dal cliente
                        </span>
                      </div>
                    )}
                    
                    {assessment.compilato_da_cliente && !assessment.word_generato && (
                      <div className="flex items-center gap-2 text-sm">
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        <span className="text-blue-700 font-medium">
                          Compilato - Generazione documento in corso
                        </span>
                      </div>
                    )}
                    
                    {assessment.word_generato && assessment.stato !== 'approvato' && (
                      <div className="flex items-center gap-2 text-sm">
                        <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                        <span className="text-orange-700 font-medium">
                          Pronto per revisione e approvazione
                        </span>
                      </div>
                    )}
                    
                    {assessment.stato === 'approvato' && (
                      <div className="flex items-center gap-2 text-sm">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span className="text-green-700 font-medium">
                          Approvato - PDF disponibile
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="flex gap-2">
                  {!assessment.compilato_da_cliente && (
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => window.open(`${CLIENT_BASE_URL}/client/${assessment.slug}`, '_blank')}
                      className="bg-green-600 hover:bg-green-700 text-white"
                      data-testid={`open-client-link-${assessment.id}`}
                    >
                      <ExternalLink className="w-4 h-4 mr-1" />
                      Apri Link Cliente
                    </Button>
                  )}
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onCopyLink(assessment.slug)}
                    className="border-blue-600 text-blue-600 hover:bg-blue-50"
                    data-testid={`copy-link-${assessment.id}`}
                  >
                    <Copy className="w-4 h-4 mr-1" />
                    Copia Link
                  </Button>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onView(assessment)}
                    data-testid={`view-assessment-${assessment.id}`}
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:text-red-700"
                        data-testid={`delete-assessment-${assessment.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Elimina Assessment</AlertDialogTitle>
                        <AlertDialogDescription>
                          Sei sicuro di voler eliminare questo assessment? Questa azione non può essere annullata.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Annulla</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => onDelete(assessment.id)}
                          className="bg-red-600 hover:bg-red-700"
                        >
                          Elimina
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
};

// Assessment Detail Component for Admin
const AssessmentDetail = ({ assessment, onBack, onApproval, onReload }) => {
  const [notes, setNotes] = useState(assessment.note_revisione || "");

  useEffect(() => {
    setNotes(assessment.note_revisione || "");
  }, [assessment]);

  const handleDownload = async (format) => {
    try {
      const response = await axios.get(`${API}/assessments/${assessment.id}/contract/${format}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = format === 'word' 
        ? `bozza_contratto_${assessment.id}.docx`
        : `contratto_finale_${assessment.id}.pdf`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Contratto ${format.toUpperCase()} scaricato con successo!`);
    } catch (error) {
      console.error("Error downloading contract:", error);
      if (error.response?.status === 400) {
        toast.error(error.response.data.detail);
      } else {
        toast.error(`Errore nel download del contratto ${format.toUpperCase()}`);
      }
    }
  };

  const handleApprove = async (newStatus) => {
    await onApproval(assessment.id, newStatus, notes);
    await onReload();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-6">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="outline"
            onClick={onBack}
            className="flex items-center gap-2"
            data-testid="back-to-list-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Torna alla Lista
          </Button>
          
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-slate-800">{assessment.titolo}</h1>
            <div className="flex items-center gap-2 mt-2">
              <Badge className={
                assessment.stato === 'approvato' ? 'bg-green-100 text-green-800' :
                assessment.stato === 'compilato_cliente' ? 'bg-yellow-100 text-yellow-800' :
                'bg-gray-100 text-gray-800'
              }>
                {assessment.stato === 'creato' ? 'Da compilare' :
                 assessment.stato === 'compilato_cliente' ? 'Compilato dal cliente' :
                 assessment.stato === 'in_revisione' ? 'In revisione' :
                 assessment.stato === 'approvato' ? 'Approvato' : assessment.stato}
              </Badge>
              <span className="text-slate-500">
                Creato il {formatDateDisplay(assessment.data_creazione)}
              </span>
            </div>
          </div>
          
          <div className="flex gap-2">
            {assessment.compilato_da_cliente && (
              <Button
                onClick={() => handleDownload('word')}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
                data-testid="download-word-btn"
              >
                <Download className="w-4 h-4" />
                Bozza Word
              </Button>
            )}
            
            {assessment.stato === 'approvato' && (
              <Button
                onClick={() => handleDownload('pdf')}
                className="flex items-center gap-2 bg-red-600 hover:bg-red-700"
                data-testid="download-pdf-btn"
              >
                <Download className="w-4 h-4" />
                PDF Finale
              </Button>
            )}
          </div>
        </div>

        {/* Status and Actions */}
        {assessment.compilato_da_cliente && assessment.stato !== 'approvato' && (
          <Card className="mb-6 border-yellow-200 bg-yellow-50">
            <CardHeader>
              <CardTitle className="text-yellow-800">Revisione Assessment</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="notes">Note di Revisione</Label>
                <Textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Aggiungi note per la revisione..."
                  rows={3}
                />
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => handleApprove('in_revisione')}
                  variant="outline"
                  className="border-blue-600 text-blue-600 hover:bg-blue-50"
                >
                  <Clock className="w-4 h-4 mr-2" />
                  Metti in Revisione
                </Button>
                <Button
                  onClick={() => handleApprove('approvato')}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Approva Assessment
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Content Display */}
        {assessment.compilato_da_cliente ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Rest of the detail view content... */}
            <Card className="shadow-lg border-0">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  Società Destinataria
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label className="text-slate-600">Denominazione</Label>
                  <p className="font-medium">{assessment.societa_destinataria.denominazione}</p>
                </div>
                <div>
                  <Label className="text-slate-600">Partita IVA</Label>
                  <p className="font-medium">{assessment.societa_destinataria.partita_iva}</p>
                </div>
                <div>
                  <Label className="text-slate-600">Sede</Label>
                  <p className="font-medium">
                    {assessment.societa_destinataria.indirizzo_sede}, {assessment.societa_destinataria.cap} {assessment.societa_destinataria.citta_provincia}
                  </p>
                </div>
                <div>
                  <Label className="text-slate-600">Legale Rappresentante</Label>
                  <p className="font-medium">{assessment.societa_destinataria.legale_rappresentante_nome}</p>
                  {assessment.societa_destinataria.legale_rappresentante_cf && (
                    <p className="text-sm text-slate-500">CF: {assessment.societa_destinataria.legale_rappresentante_cf}</p>
                  )}
                  {assessment.societa_destinataria.legale_rappresentante_luogo_nascita && assessment.societa_destinataria.legale_rappresentante_data_nascita && (
                    <p className="text-sm text-slate-500">
                      Nato a {assessment.societa_destinataria.legale_rappresentante_luogo_nascita} il {formatDateDisplay(assessment.societa_destinataria.legale_rappresentante_data_nascita)}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
            
            <Card className="shadow-lg border-0">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Beneficiari ({assessment.beneficiari.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {assessment.beneficiari.map((beneficiario, index) => (
                  <div key={beneficiario.id} className="p-3 bg-slate-50 rounded-lg">
                    {beneficiario.tipo === "persona_fisica" && beneficiario.persona_fisica && (
                      <>
                        <p className="font-medium">
                          {beneficiario.persona_fisica.nome} {beneficiario.persona_fisica.cognome}
                        </p>
                        <p className="text-sm text-slate-600">
                          CF: {beneficiario.persona_fisica.codice_fiscale}
                        </p>
                      </>
                    )}
                    {beneficiario.tipo === "persona_giuridica" && beneficiario.persona_giuridica && (
                      <>
                        <p className="font-medium">{beneficiario.persona_giuridica.ragione_sociale}</p>
                        <p className="text-sm text-slate-600">
                          P.IVA: {beneficiario.persona_giuridica.partita_iva}
                        </p>
                      </>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
            
            <Card className="shadow-lg border-0 lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Contratti e Attività
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {assessment.attivita_contratti.map((attivita, index) => {
                  const beneficiario = assessment.beneficiari.find(b => b.id === attivita.beneficiario_id);
                  const beneficiarioName = beneficiario?.tipo === "persona_fisica" 
                    ? `${beneficiario.persona_fisica?.nome} ${beneficiario.persona_fisica?.cognome}`
                    : beneficiario?.persona_giuridica?.ragione_sociale || "Beneficiario";
                  
                  return (
                    <div key={attivita.id} className="p-4 bg-slate-50 rounded-lg space-y-3">
                      <h4 className="font-medium text-lg">{beneficiarioName}</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div>
                          <Label className="text-slate-600">Durata</Label>
                          <p className="font-medium">{attivita.durata_mesi} mesi</p>
                        </div>
                        <div>
                          <Label className="text-slate-600">Importo Obiettivo</Label>
                          <p className="font-medium">€ {attivita.importo_obiettivo.toLocaleString()}</p>
                        </div>
                        {attivita.numero_ore_previste && (
                          <div>
                            <Label className="text-slate-600">Ore Previste</Label>
                            <p className="font-medium">{attivita.numero_ore_previste.toLocaleString()} ore</p>
                          </div>
                        )}
                        {attivita.costo_orario && (
                          <div>
                            <Label className="text-slate-600">Costo Orario</Label>
                            <p className="font-medium">€ {attivita.costo_orario.toFixed(2)}/ora</p>
                          </div>
                        )}
                        <div>
                          <Label className="text-slate-600">Equity</Label>
                          <p className="font-medium">{attivita.equity_percentuale}%</p>
                        </div>
                      </div>
                      <div>
                        <Label className="text-slate-600">Attività Principale</Label>
                        <p className="text-sm">{attivita.descrizione_attivita_1}</p>
                      </div>
                      {attivita.cliff_descrizione && (
                        <div>
                          <Label className="text-slate-600">Clausola Cliff</Label>
                          <p className="text-sm">{attivita.cliff_descrizione}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </CardContent>
            </Card>
            
            <Card className="shadow-lg border-0 lg:col-span-2">
            <CardHeader>
              <CardTitle>Valutazione Complessiva</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-slate-600">Valore Premoney</Label>
                  <p className="font-medium text-lg">
                    € {assessment.valutazione_complessiva.valore_premoney.toLocaleString()}
                  </p>
                </div>
                <div>
                  <Label className="text-slate-600">AUCAP Deliberato</Label>
                  <p className="font-medium text-lg">
                    {assessment.valutazione_complessiva.aucap_deliberato ? "SI" : "NO"}
                  </p>
                </div>
              </div>
              
              {assessment.valutazione_complessiva.aucap_deliberato ? (
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h4 className="font-medium text-blue-800 mb-3">AUCAP Deliberato</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {assessment.valutazione_complessiva.aucap_include_wfe !== null && (
                      <div>
                        <Label className="text-slate-600">Include WFE</Label>
                        <p className="font-medium">
                          {assessment.valutazione_complessiva.aucap_include_wfe ? "SI" : "NO"}
                        </p>
                      </div>
                    )}
                    {assessment.valutazione_complessiva.data_atto_aucap && (
                      <div>
                        <Label className="text-slate-600">Data Atto AUCAP</Label>
                        <p className="font-medium">
                          {formatDateDisplay(assessment.valutazione_complessiva.data_atto_aucap)}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                  <h4 className="font-medium text-yellow-800 mb-3">AUCAP da Deliberare</h4>
                  {assessment.valutazione_complessiva.data_limite_aucap && (
                    <div>
                      <Label className="text-slate-600">Data Limite AUCAP</Label>
                      <p className="font-medium">
                        {formatDateDisplay(assessment.valutazione_complessiva.data_limite_aucap)}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
          </div>
        ) : (
          <Card className="text-center py-12">
            <CardContent>
              <Clock className="w-16 h-16 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-600 mb-2">
                In Attesa di Compilazione
              </h3>
              <p className="text-slate-500 mb-6">
                L'assessment non è ancora stato compilato dal cliente.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-md mx-auto">
                <p className="text-blue-800 text-sm mb-2">
                  <strong>Link per il cliente:</strong>
                </p>
                <code className="text-blue-600 text-xs break-all">
                  {window.location.origin}/client/{assessment.slug}
                </code>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

// Step Components (same as before but simplified for client use)
const StepSocieta = ({ data, onChange }) => {
  const [validationErrors, setValidationErrors] = useState({});

  const updateField = (field, value) => {
    const newData = { ...data, [field]: value };
    
    // Validate fiscal code and VAT number
    const errors = { ...validationErrors };
    
    if (field === 'partita_iva' && value) {
      if (!validatePartitaIVA(value)) {
        errors.partita_iva = 'Partita IVA non valida';
      } else {
        delete errors.partita_iva;
      }
    }
    
    if (field === 'legale_rappresentante_cf' && value) {
      if (!validateCodiceFiscale(value)) {
        errors.legale_rappresentante_cf = 'Codice fiscale non valido';
      } else {
        delete errors.legale_rappresentante_cf;
      }
    }
    
    setValidationErrors(errors);
    onChange(newData);
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-semibold text-slate-800">Dati Società</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <Label htmlFor="denominazione">Denominazione *</Label>
          <Input
            id="denominazione"
            value={data.denominazione}
            onChange={(e) => updateField("denominazione", e.target.value)}
            placeholder="Es: ABC S.R.L."
          />
        </div>
        
        <div>
          <Label htmlFor="partita_iva">Partita IVA *</Label>
          <Input
            id="partita_iva"
            value={data.partita_iva}
            onChange={(e) => updateField("partita_iva", e.target.value)}
            placeholder="Es: 12345678901"
            className={validationErrors.partita_iva ? "border-red-500" : ""}
          />
          {validationErrors.partita_iva && (
            <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {validationErrors.partita_iva}
            </p>
          )}
        </div>
        
        <div className="md:col-span-2">
          <Label htmlFor="indirizzo_sede">Indirizzo Sede Legale *</Label>
          <Input
            id="indirizzo_sede"
            value={data.indirizzo_sede}
            onChange={(e) => updateField("indirizzo_sede", e.target.value)}
            placeholder="Es: Via Roma 123"
          />
        </div>
        
        <div>
          <Label htmlFor="cap">CAP *</Label>
          <Input
            id="cap"
            value={data.cap}
            onChange={(e) => updateField("cap", e.target.value)}
            placeholder="Es: 00100"
          />
        </div>
        
        <div>
          <Label htmlFor="citta_provincia">Città e Provincia *</Label>
          <Input
            id="citta_provincia"
            value={data.citta_provincia}
            onChange={(e) => updateField("citta_provincia", e.target.value)}
            placeholder="Es: Roma (RM)"
          />
        </div>
        
        <div className="md:col-span-2">
          <Label htmlFor="legale_rappresentante_nome">Nome Legale Rappresentante *</Label>
          <Input
            id="legale_rappresentante_nome"
            value={data.legale_rappresentante_nome}
            onChange={(e) => updateField("legale_rappresentante_nome", e.target.value)}
            placeholder="Es: Mario Rossi"
          />
        </div>
        
        <div>
          <Label htmlFor="legale_rappresentante_cf">Codice Fiscale Legale Rappresentante *</Label>
          <Input
            id="legale_rappresentante_cf"
            value={data.legale_rappresentante_cf}
            onChange={(e) => updateField("legale_rappresentante_cf", e.target.value)}
            placeholder="Es: RSSMRA80A01H501Z"
            className={validationErrors.legale_rappresentante_cf ? "border-red-500" : ""}
          />
          {validationErrors.legale_rappresentante_cf && (
            <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {validationErrors.legale_rappresentante_cf}
            </p>
          )}
        </div>
        
        <div>
          <Label htmlFor="legale_rappresentante_luogo_nascita">Luogo di Nascita *</Label>
          <Input
            id="legale_rappresentante_luogo_nascita"
            value={data.legale_rappresentante_luogo_nascita}
            onChange={(e) => updateField("legale_rappresentante_luogo_nascita", e.target.value)}
            placeholder="Es: Roma (RM)"
          />
        </div>
        
        <div>
          <Label htmlFor="legale_rappresentante_data_nascita">Data di Nascita *</Label>
          <Input
            id="legale_rappresentante_data_nascita"
            type="date"
            value={data.legale_rappresentante_data_nascita}
            onChange={(e) => updateField("legale_rappresentante_data_nascita", e.target.value)}
          />
        </div>
        
        <div>
          <Label htmlFor="mail_pec">Mail PEC *</Label>
          <Input
            id="mail_pec"
            value={data.mail_pec}
            onChange={(e) => updateField("mail_pec", e.target.value)}
            placeholder="Es: societa@pec.it"
          />
        </div>
        
        <div>
          <Label htmlFor="codice_sdi">Codice SDI</Label>
          <Input
            id="codice_sdi"
            value={data.codice_sdi}
            onChange={(e) => updateField("codice_sdi", e.target.value)}
            placeholder="Es: XXXXXXX"
          />
        </div>
      </div>
    </div>
  );
};

const StepBeneficiari = ({ data, onChange, onAdd, onRemove }) => {
  const [validationErrors, setValidationErrors] = useState({});

  const updateBeneficiario = (index, field, value) => {
    const updated = [...data];
    const errors = { ...validationErrors };
    const errorKey = `${index}_${field}`;

    if (field === "tipo") {
      updated[index] = {
        ...updated[index],
        tipo: value,
        persona_fisica: value === "persona_fisica" ? {
          nome: "", cognome: "", codice_fiscale: "", luogo_nascita: "",
          data_nascita: "", indirizzo_domicilio: "", mail_pec: "", mail_ordinaria: ""
        } : null,
        persona_giuridica: value === "persona_giuridica" ? {
          ragione_sociale: "", codice_fiscale: "", partita_iva: "", indirizzo_sede: "",
          mail_pec: "", mail_ordinaria: "", 
          legale_rappresentante_nome: "", legale_rappresentante_cognome: "",
          legale_rappresentante_cf: "", legale_rappresentante_luogo_nascita: "",
          legale_rappresentante_data_nascita: ""
        } : null
      };
    } else if (updated[index].tipo === "persona_fisica") {
      updated[index].persona_fisica = { ...updated[index].persona_fisica, [field]: value };
      
      // Validate fiscal code for persona fisica
      if (field === 'codice_fiscale' && value) {
        if (!validateCodiceFiscale(value)) {
          errors[errorKey] = 'Codice fiscale non valido';
        } else {
          delete errors[errorKey];
        }
      }
    } else if (updated[index].tipo === "persona_giuridica") {
      updated[index].persona_giuridica = { ...updated[index].persona_giuridica, [field]: value };
      
      // Validate only VAT and legal representative fiscal code for persona giuridica
      if (field === 'partita_iva' && value) {
        if (!validatePartitaIVA(value)) {
          errors[`${index}_partita_iva`] = 'Partita IVA non valida';
        } else {
          delete errors[`${index}_partita_iva`];
        }
      }
      // Validate legal representative fiscal code
      if (field === 'legale_rappresentante_cf' && value) {
        if (!validateCodiceFiscale(value)) {
          errors[`${index}_legale_rappresentante_cf`] = 'Codice fiscale del legale rappresentante non valido';
        } else {
          delete errors[`${index}_legale_rappresentante_cf`];
        }
      }
    }
    
    setValidationErrors(errors);
    onChange(updated);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold text-slate-800">Beneficiari</h2>
        <Button
          onClick={onAdd}
          className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
        >
          <PlusCircle className="w-4 h-4" />
          Aggiungi Beneficiario
        </Button>
      </div>
      
      {data.length === 0 ? (
        <div className="text-center py-8 text-slate-500">
          <Users className="w-12 h-12 mx-auto mb-4 text-slate-300" />
          <p>Nessun beneficiario aggiunto. Clicca "Aggiungi Beneficiario" per iniziare.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {data.map((beneficiario, index) => (
            <Card key={beneficiario.id} className="border-slate-200">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">
                  Beneficiario {index + 1}
                </CardTitle>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => onRemove(beneficiario.id)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor={`tipo-beneficiario-${index}`}>Tipo Beneficiario</Label>
                  <Select
                    value={beneficiario.tipo}
                    onValueChange={(value) => updateBeneficiario(index, "tipo", value)}
                  >
                    <SelectTrigger id={`tipo-beneficiario-${index}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="persona_fisica">Persona Fisica</SelectItem>
                      <SelectItem value="persona_giuridica">Persona Giuridica</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {beneficiario.tipo === "persona_fisica" && beneficiario.persona_fisica && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor={`nome-${index}`}>Nome *</Label>
                      <Input
                        id={`nome-${index}`}
                        value={beneficiario.persona_fisica.nome}
                        onChange={(e) => updateBeneficiario(index, "nome", e.target.value)}
                        placeholder="Nome"
                      />
                    </div>
                    <div>
                      <Label htmlFor={`cognome-${index}`}>Cognome *</Label>
                      <Input
                        id={`cognome-${index}`}
                        value={beneficiario.persona_fisica.cognome}
                        onChange={(e) => updateBeneficiario(index, "cognome", e.target.value)}
                        placeholder="Cognome"
                      />
                    </div>
                    <div>
                      <Label htmlFor={`cf-${index}`}>Codice Fiscale *</Label>
                      <Input
                        id={`cf-${index}`}
                        value={beneficiario.persona_fisica.codice_fiscale}
                        onChange={(e) => updateBeneficiario(index, "codice_fiscale", e.target.value)}
                        placeholder="RSSMRA80A01H501Z"
                        className={validationErrors[`${index}_codice_fiscale`] ? "border-red-500" : ""}
                      />
                      {validationErrors[`${index}_codice_fiscale`] && (
                        <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                          <AlertCircle className="w-4 h-4" />
                          {validationErrors[`${index}_codice_fiscale`]}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor={`luogo-nascita-${index}`}>Luogo di Nascita *</Label>
                      <Input
                        id={`luogo-nascita-${index}`}
                        value={beneficiario.persona_fisica.luogo_nascita}
                        onChange={(e) => updateBeneficiario(index, "luogo_nascita", e.target.value)}
                        placeholder="Roma (RM)"
                      />
                    </div>
                    <div>
                      <Label htmlFor={`data-nascita-${index}`}>Data di Nascita *</Label>
                      <Input
                        id={`data-nascita-${index}`}
                        type="date"
                        value={beneficiario.persona_fisica.data_nascita}
                        onChange={(e) => updateBeneficiario(index, "data_nascita", e.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor={`pec-${index}`}>Mail PEC *</Label>
                      <Input
                        id={`pec-${index}`}
                        value={beneficiario.persona_fisica.mail_pec}
                        onChange={(e) => updateBeneficiario(index, "mail_pec", e.target.value)}
                        placeholder="email@pec.it"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label htmlFor={`indirizzo-${index}`}>Indirizzo Domicilio *</Label>
                      <Input
                        id={`indirizzo-${index}`}
                        value={beneficiario.persona_fisica.indirizzo_domicilio}
                        onChange={(e) => updateBeneficiario(index, "indirizzo_domicilio", e.target.value)}
                        placeholder="Via Roma 123, 00100 Roma (RM)"
                      />
                    </div>
                  </div>
                )}
                
                {beneficiario.tipo === "persona_giuridica" && beneficiario.persona_giuridica && (
                  <div className="space-y-4">
                    <h4 className="text-md font-medium text-slate-700">Dati Società</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <Label htmlFor={`ragione-sociale-${index}`}>Ragione Sociale *</Label>
                        <Input
                          id={`ragione-sociale-${index}`}
                          value={beneficiario.persona_giuridica.ragione_sociale}
                          onChange={(e) => updateBeneficiario(index, "ragione_sociale", e.target.value)}
                          placeholder="ABC S.R.L."
                        />
                      </div>
                      <div>
                        <Label htmlFor={`cf-pg-${index}`}>Codice Fiscale Società</Label>
                        <Input
                          id={`cf-pg-${index}`}
                          value={beneficiario.persona_giuridica.codice_fiscale}
                          onChange={(e) => updateBeneficiario(index, "codice_fiscale", e.target.value)}
                          placeholder="12345678901"
                        />
                        <p className="text-xs text-slate-500 mt-1">Codice fiscale della società (no controllo automatico)</p>
                      </div>
                      <div>
                        <Label htmlFor={`piva-pg-${index}`}>Partita IVA *</Label>
                        <Input
                          id={`piva-pg-${index}`}
                          value={beneficiario.persona_giuridica.partita_iva}
                          onChange={(e) => updateBeneficiario(index, "partita_iva", e.target.value)}
                          placeholder="12345678901"
                          className={validationErrors[`${index}_partita_iva`] ? "border-red-500" : ""}
                        />
                        {validationErrors[`${index}_partita_iva`] && (
                          <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                            <AlertCircle className="w-4 h-4" />
                            {validationErrors[`${index}_partita_iva`]}
                          </p>
                        )}
                      </div>
                      <div className="md:col-span-2">
                        <Label htmlFor={`sede-pg-${index}`}>Indirizzo Sede *</Label>
                        <Input
                          id={`sede-pg-${index}`}
                          value={beneficiario.persona_giuridica.indirizzo_sede}
                          onChange={(e) => updateBeneficiario(index, "indirizzo_sede", e.target.value)}
                          placeholder="Via Roma 123, 00100 Roma (RM)"
                        />
                      </div>
                      <div>
                        <Label htmlFor={`pec-pg-${index}`}>Mail PEC *</Label>
                        <Input
                          id={`pec-pg-${index}`}
                          value={beneficiario.persona_giuridica.mail_pec}
                          onChange={(e) => updateBeneficiario(index, "mail_pec", e.target.value)}
                          placeholder="email@pec.it"
                        />
                      </div>
                    </div>
                    
                    <h4 className="text-md font-medium text-slate-700 pt-4">Dati Legale Rappresentante</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor={`nome-lr-${index}`}>Nome Legale Rappresentante *</Label>
                        <Input
                          id={`nome-lr-${index}`}
                          value={beneficiario.persona_giuridica.legale_rappresentante_nome}
                          onChange={(e) => updateBeneficiario(index, "legale_rappresentante_nome", e.target.value)}
                          placeholder="Mario"
                        />
                      </div>
                      <div>
                        <Label htmlFor={`cognome-lr-${index}`}>Cognome Legale Rappresentante *</Label>
                        <Input
                          id={`cognome-lr-${index}`}
                          value={beneficiario.persona_giuridica.legale_rappresentante_cognome}
                          onChange={(e) => updateBeneficiario(index, "legale_rappresentante_cognome", e.target.value)}
                          placeholder="Rossi"
                        />
                      </div>
                      <div>
                        <Label htmlFor={`cf-lr-${index}`}>Codice Fiscale Legale Rappresentante *</Label>
                        <Input
                          id={`cf-lr-${index}`}
                          value={beneficiario.persona_giuridica.legale_rappresentante_cf}
                          onChange={(e) => updateBeneficiario(index, "legale_rappresentante_cf", e.target.value)}
                          placeholder="RSSMRA80A01H501Z"
                          className={validationErrors[`${index}_legale_rappresentante_cf`] ? "border-red-500" : ""}
                        />
                        {validationErrors[`${index}_legale_rappresentante_cf`] && (
                          <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                            <AlertCircle className="w-4 h-4" />
                            {validationErrors[`${index}_legale_rappresentante_cf`]}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor={`luogo-lr-${index}`}>Luogo di Nascita Legale Rappresentante *</Label>
                        <Input
                          id={`luogo-lr-${index}`}
                          value={beneficiario.persona_giuridica.legale_rappresentante_luogo_nascita}
                          onChange={(e) => updateBeneficiario(index, "legale_rappresentante_luogo_nascita", e.target.value)}
                          placeholder="Roma (RM)"
                        />
                      </div>
                      <div>
                        <Label htmlFor={`data-lr-${index}`}>Data di Nascita Legale Rappresentante *</Label>
                        <Input
                          id={`data-lr-${index}`}
                          type="date"
                          value={beneficiario.persona_giuridica.legale_rappresentante_data_nascita}
                          onChange={(e) => updateBeneficiario(index, "legale_rappresentante_data_nascita", e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

const StepAttivita = ({ beneficiari, attivita, onChange, onAdd }) => {
  const updateAttivita = (index, field, value) => {
    const updated = [...attivita];
    updated[index] = { ...updated[index], [field]: value };
    onChange(updated);
  };

  const getBeneficiarioName = (beneficiarioId) => {
    const beneficiario = beneficiari.find(b => b.id === beneficiarioId);
    if (!beneficiario) return "Beneficiario non trovato";
    
    if (beneficiario.tipo === "persona_fisica" && beneficiario.persona_fisica) {
      return `${beneficiario.persona_fisica.nome} ${beneficiario.persona_fisica.cognome}`;
    } else if (beneficiario.tipo === "persona_giuridica" && beneficiario.persona_giuridica) {
      return beneficiario.persona_giuridica.ragione_sociale;
    }
    return "Beneficiario";
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-semibold text-slate-800">Attività e Contratti</h2>
      
      {beneficiari.length === 0 ? (
        <div className="text-center py-8 text-slate-500">
          <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
          <p>Aggiungi prima almeno un beneficiario per definire le attività.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {beneficiari.map((beneficiario) => {
            const beneficiarioAttivita = attivita.find(a => a.beneficiario_id === beneficiario.id);
            
            return (
              <Card key={beneficiario.id} className="border-slate-200">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-lg">
                    Contratto per: {getBeneficiarioName(beneficiario.id)}
                  </CardTitle>
                  {!beneficiarioAttivita && (
                    <Button
                      onClick={() => onAdd(beneficiario.id)}
                      className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
                    >
                      <PlusCircle className="w-4 h-4" />
                      Aggiungi Contratto
                    </Button>
                  )}
                </CardHeader>
                
                {beneficiarioAttivita && (
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <Label htmlFor={`attivita1-${beneficiario.id}`}>Descrizione Attività 1 *</Label>
                        <Textarea
                          id={`attivita1-${beneficiario.id}`}
                          value={beneficiarioAttivita.descrizione_attivita_1}
                          onChange={(e) => {
                            const index = attivita.findIndex(a => a.id === beneficiarioAttivita.id);
                            updateAttivita(index, "descrizione_attivita_1", e.target.value);
                          }}
                          placeholder="Descrivi le attività principali da svolgere..."
                          rows={3}
                        />
                      </div>
                      
                      <div className="md:col-span-2">
                        <Label htmlFor={`attivita2-${beneficiario.id}`}>Descrizione Attività 2</Label>
                        <Textarea
                          id={`attivita2-${beneficiario.id}`}
                          value={beneficiarioAttivita.descrizione_attivita_2}
                          onChange={(e) => {
                            const index = attivita.findIndex(a => a.id === beneficiarioAttivita.id);
                            updateAttivita(index, "descrizione_attivita_2", e.target.value);
                          }}
                          placeholder="Descrivi attività aggiuntive (opzionale)..."
                          rows={2}
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor={`durata-${beneficiario.id}`}>Durata (mesi) *</Label>
                        <Input
                          id={`durata-${beneficiario.id}`}
                          type="number"
                          value={beneficiarioAttivita.durata_mesi}
                          onChange={(e) => {
                            const index = attivita.findIndex(a => a.id === beneficiarioAttivita.id);
                            updateAttivita(index, "durata_mesi", parseInt(e.target.value) || 0);
                          }}
                          placeholder="6"
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor={`importo-${beneficiario.id}`}>Importo Obiettivo (€) *</Label>
                        <Input
                          id={`importo-${beneficiario.id}`}
                          type="number"
                          value={beneficiarioAttivita.importo_obiettivo || ""}
                          onChange={(e) => {
                            const index = attivita.findIndex(a => a.id === beneficiarioAttivita.id);
                            updateAttivita(index, "importo_obiettivo", Number(e.target.value) || 0);
                          }}
                          placeholder="200000"
                        />
                        <p className="text-sm text-slate-500 mt-1">
                          Valore della prestazione
                        </p>
                      </div>
                      
                      <div>
                        <Label htmlFor={`ore-${beneficiario.id}`}>Numero Ore Previste *</Label>
                        <Input
                          id={`ore-${beneficiario.id}`}
                          type="number"
                          step="0.5"
                          value={beneficiarioAttivita.numero_ore_previste || ""}
                          onChange={(e) => {
                            const index = attivita.findIndex(a => a.id === beneficiarioAttivita.id);
                            updateAttivita(index, "numero_ore_previste", Number(e.target.value) || 0);
                          }}
                          placeholder="1600"
                        />
                        <p className="text-sm text-slate-500 mt-1">
                          Numero totale di ore di lavoro previste
                        </p>
                      </div>
                      
                      <div>
                        <Label htmlFor={`costo-orario-${beneficiario.id}`}>Costo Orario (€/ora)</Label>
                        <Input
                          id={`costo-orario-${beneficiario.id}`}
                          type="number"
                          step="0.01"
                          value={beneficiarioAttivita.costo_orario || ""}
                          readOnly
                          className="bg-slate-100 cursor-not-allowed"
                          placeholder="125.00"
                        />
                        <p className="text-sm text-slate-500 mt-1">
                          Calcolato automaticamente: Importo Obiettivo ÷ Numero Ore Previste
                        </p>
                      </div>
                      
                      <div>
                        <Label htmlFor={`equity-${beneficiario.id}`}>Equity Percentuale (%) *</Label>
                        <Input
                          id={`equity-${beneficiario.id}`}
                          type="number"
                          step="0.1"
                          value={beneficiarioAttivita.equity_percentuale}
                          onChange={(e) => {
                            const index = attivita.findIndex(a => a.id === beneficiarioAttivita.id);
                            updateAttivita(index, "equity_percentuale", parseFloat(e.target.value) || 0);
                          }}
                          placeholder="3.0"
                        />
                      </div>
                      
                      <div className="md:col-span-2">
                        <Label htmlFor={`cliff-${beneficiario.id}`}>Clausola Cliff</Label>
                        <Textarea
                          id={`cliff-${beneficiario.id}`}
                          value={beneficiarioAttivita.cliff_descrizione}
                          onChange={(e) => {
                            const index = attivita.findIndex(a => a.id === beneficiarioAttivita.id);
                            updateAttivita(index, "cliff_descrizione", e.target.value);
                          }}
                          placeholder="Descrivi le condizioni di permanenza o cliff..."
                          rows={2}
                        />
                      </div>
                      
                      <div className="md:col-span-2">
                        <Label htmlFor={`performance-${beneficiario.id}`}>Condizioni Performance</Label>
                        <Textarea
                          id={`performance-${beneficiario.id}`}
                          value={beneficiarioAttivita.performance_descrizione}
                          onChange={(e) => {
                            const index = attivita.findIndex(a => a.id === beneficiarioAttivita.id);
                            updateAttivita(index, "performance_descrizione", e.target.value);
                          }}
                          placeholder="Descrivi obiettivi e performance richieste..."
                          rows={2}
                        />
                      </div>
                    </div>
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

const StepValutazione = ({ data, onChange }) => {
  const updateField = (field, value) => {
    const newData = { ...data, [field]: value };
    
    // Se cambia lo stato di deliberato, reset dei campi correlati
    if (field === "aucap_deliberato") {
      if (value) {
        // Se deliberato = SI, reset data_limite_aucap
        newData.data_limite_aucap = "";
      } else {
        // Se deliberato = NO, reset campi del SI
        newData.aucap_include_wfe = null;
        newData.data_atto_aucap = "";
      }
    }
    
    onChange(newData);
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-semibold text-slate-800">Valutazione Complessiva</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <Label htmlFor="valore_premoney">Valore Premoney (€) *</Label>
          <Input
            id="valore_premoney"
            type="number"
            value={data.valore_premoney}
            onChange={(e) => updateField("valore_premoney", parseFloat(e.target.value) || 0)}
            placeholder="6666667"
          />
          <p className="text-sm text-slate-500 mt-1">
            Valore della società prima dell'investimento/equity
          </p>
        </div>
        
        <div>
          <Label htmlFor="aucap_deliberato">L'AUCAP è già stato deliberato? *</Label>
          <Select
            value={data.aucap_deliberato ? "true" : "false"}
            onValueChange={(value) => updateField("aucap_deliberato", value === "true")}
          >
            <SelectTrigger id="aucap_deliberato">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="false">NO</SelectItem>
              <SelectItem value="true">SI</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Campi condizionali per AUCAP deliberato = SI */}
      {data.aucap_deliberato && (
        <div className="space-y-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h3 className="text-lg font-medium text-blue-800">AUCAP già deliberato</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="aucap_include_wfe">L'AUCAP include già una tranche per il work for equity? *</Label>
              <Select
                value={data.aucap_include_wfe === null ? "" : data.aucap_include_wfe ? "true" : "false"}
                onValueChange={(value) => updateField("aucap_include_wfe", value === "" ? null : value === "true")}
              >
                <SelectTrigger id="aucap_include_wfe">
                  <SelectValue placeholder="Seleziona una risposta" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="false">NO</SelectItem>
                  <SelectItem value="true">SI</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="data_atto_aucap">Data Atto di AUCAP *</Label>
              <Input
                id="data_atto_aucap"
                type="date"
                value={data.data_atto_aucap}
                onChange={(e) => updateField("data_atto_aucap", e.target.value)}
              />
              <p className="text-sm text-slate-500 mt-1">
                Data dell'atto di aumento di capitale
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Campo per AUCAP deliberato = NO */}
      {!data.aucap_deliberato && (
        <div className="space-y-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
          <h3 className="text-lg font-medium text-yellow-800">AUCAP da deliberare</h3>
          <div>
            <Label htmlFor="data_limite_aucap">Data Limite per l'AUCAP *</Label>
            <Input
              id="data_limite_aucap"
              type="date"
              value={data.data_limite_aucap}
              onChange={(e) => updateField("data_limite_aucap", e.target.value)}
            />
            <p className="text-sm text-slate-500 mt-1">
              Data limite per l'aumento di capitale
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<AdminDashboard />} />
        <Route path="/client/:slug" element={<ClientAssessmentForm />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;